import {FC, useEffect, useMemo} from "react"

import {
  toCaseInfoInput,
  toReplacementEstimateInput,
  buildCaseInfo,
} from "@frontend/utils/appHostFunctions"
import {setHostCaseInfo} from "@frontend/utils/hostCaseInfo"
import {
  emitHostRecommendationGenerated,
  remediationTypeToHostJourneyStep,
} from "@frontend/utils/hostJourneyEvents"
import {trackRecommendationOutcome} from "@frontend/utils/outcomeTracking"
import {trpc} from "@frontend/utils/trpc"
import {useBooleanState} from "@frontend/utils/useBooleanState"
import {useHostReplacementEstimate} from "@frontend/utils/useHostReplacementEstimate"
import {css} from "@styled-system/css"

import {DesktopRemediations} from "./desktop-remediations"
import {LargeDesktopRemediations} from "./large-desktop-remediations"
import {LearnMoreBasedOnModal} from "./learn-more-based-on-modal"
import {MobileRemediations} from "./mobile-remediations"
import {RemediationProps} from "./types"

type Props = Omit<RemediationProps, "openModal">

export const Remediations: FC<Props> = (props) => {
  const {
    state: isOpenModal,
    setFalse: closeModal,
    setTrue: openModal,
  } = useBooleanState(false)

  const {
    caseModelId,
    causeOnMessageId,
    pageData: {productInfo, causes, parts, applianceAgeMonths},
  } = props

  const product = useMemo(
    () => toCaseInfoInput({productInfo, causes, parts, applianceAgeMonths}),
    [productInfo, causes, parts, applianceAgeMonths],
  )

  const {estimate, isLoading: isHostEstimateLoading} =
    useHostReplacementEstimate({
      enabled: true,
      caseId: caseModelId,
      product,
    })

  const estimateInput = toReplacementEstimateInput(estimate)
  const {data: overriddenPageData, isFetching: isServerOverrideFetching} =
    trpc.remediationPageData.useQuery(
      {
        caseId: caseModelId,
        causeOnMessageId,
        replacementEstimate: estimateInput,
      },
      {enabled: !!estimateInput},
    )

  // Keep Burke bands for DIY/DIFM; only Replace shows a loading state until
  // the host estimate is resolved and (when present) the server override lands.
  const isReplacePriceLoading =
    isHostEstimateLoading ||
    (!!estimateInput && (isServerOverrideFetching || !overriddenPageData))

  const pageData = overriddenPageData ?? props.pageData
  const recommendationType = pageData.recommendation?.type
  const updatedProps = {
    ...props,
    pageData,
    isReplacePriceLoading,
  }

  useEffect(() => {
    if (!recommendationType || isReplacePriceLoading) return

    setHostCaseInfo(
      buildCaseInfo({
        caseId: caseModelId,
        product: toCaseInfoInput({
          productInfo: pageData.productInfo,
          causes: pageData.causes,
          parts: pageData.parts,
          applianceAgeMonths: pageData.applianceAgeMonths,
        }),
      }),
    )
    emitHostRecommendationGenerated(
      caseModelId,
      remediationTypeToHostJourneyStep(recommendationType),
    )
    trackRecommendationOutcome(
      caseModelId,
      causeOnMessageId,
      recommendationType,
    )
  }, [
    caseModelId,
    causeOnMessageId,
    recommendationType,
    isReplacePriceLoading,
    pageData.productInfo,
    pageData.causes,
    pageData.parts,
    pageData.applianceAgeMonths,
  ])

  return (
    <div
      className={css({
        position: "relative",
        flexGrow: 1,
        largeDesktopDown: {
          display: "contents",
        },
      })}
    >
      <LargeDesktopRemediations {...updatedProps} openModal={openModal} />
      <MobileRemediations {...updatedProps} openModal={openModal} />
      <DesktopRemediations {...updatedProps} openModal={openModal} />
      {pageData.recommendation && (
        <LearnMoreBasedOnModal
          recommendation={pageData.recommendation}
          onDismiss={closeModal}
          show={isOpenModal}
        />
      )}
    </div>
  )
}
